import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { getDashboard, DashboardData } from '../api/franchise.api';
import styles from './DashboardPage.module.css';

function getToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function getBranchColor(currentWaiting: number): string {
  if (currentWaiting >= 20) return 'var(--color-danger)';
  if (currentWaiting >= 10) return 'var(--color-warning)';
  if (currentWaiting >= 5) return 'var(--color-primary)';
  return 'var(--color-success)';
}

export default function DashboardPage() {
  const { franchiseId, franchiseName } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!franchiseId) return;
    try {
      const result = await getDashboard(franchiseId);
      setData(result);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '데이터 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [franchiseId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <Layout title="대시보드">
        <LoadingSpinner />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="대시보드">
        <div className={styles.error}>{error}</div>
      </Layout>
    );
  }

  if (!data) return null;

  const summaryItems = [
    { label: '오늘 총 대기', value: data.totalWaiting, color: 'var(--color-primary)' },
    { label: '총 입장', value: data.totalEntered, color: 'var(--color-success)' },
    { label: '총 노쇼', value: data.totalNoShow, color: 'var(--color-danger)' },
    { label: '총 취소', value: data.totalCanceled, color: 'var(--color-warning)' },
  ];

  return (
    <Layout title="대시보드">
      <div className={styles.header}>
        <span className={styles.franchiseName}>{data.franchiseName || franchiseName}</span>
        <span className={styles.date}>{getToday()}</span>
      </div>

      {/* 요약 카드 - 지점 카드와 동일한 스타일 */}
      <div className={styles.cardGrid}>
        {summaryItems.map((item) => (
          <div key={item.label} className={styles.card} style={{ borderTop: `4px solid ${item.color}` }}>
            <div className={styles.cardLabel}>{item.label}</div>
            <div className={styles.cardValue}>{item.value}</div>
          </div>
        ))}
      </div>

      <h2 className={styles.sectionTitle}>지점별 현황</h2>
      <div className={styles.cardGrid}>
        {data.branches.map((branch) => {
          const color = getBranchColor(branch.currentWaiting);
          return (
            <div
              key={branch.storeId}
              className={styles.card}
              style={{ borderTop: `4px solid ${color}` }}
            >
              <div className={styles.cardName}>{branch.storeName}</div>
              <div className={styles.branchStats}>
                <div className={styles.branchStat}>
                  <span className={styles.branchStatLabel}>오늘 대기</span>
                  <span className={styles.branchStatValue}>{branch.todayTotal}</span>
                </div>
                <div className={styles.branchStat}>
                  <span className={styles.branchStatLabel}>현재 대기</span>
                  <span className={styles.branchStatValue} style={{ color }}>
                    {branch.currentWaiting}
                  </span>
                </div>
              </div>
              <div className={styles.branchMeta}>
                <span className={styles.metaItem}>
                  입장 <strong>{branch.entered}</strong>
                </span>
                <span className={styles.metaItem}>
                  노쇼 <strong>{branch.noShow}</strong>
                </span>
                <span className={styles.metaItem}>
                  취소 <strong>{branch.canceled}</strong>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
